import { useState } from "react";
import {
  lockedReasonKey,
  parseStaffOverrides,
  staffOverrideEmptyReasonKey,
  staffOverrideReopenBlockedKey,
  staffOverridesForStep,
  visibleStaffOverrideLocks,
  type StaffOverrideAction,
  type StaffOverrideKind,
  type StaffOverrideReopenFacts,
  type StaffOverridesValue
} from "../../shared/staff-override";
import type { StaffDetail, StaffRun } from "./model";
import "./override-panel.css";

/**
 * F104: the partner controls the client-side state DIRECTLY IN THE STEP -
 * "vybrany krok je hotovy / schvaleno" plus the two actions (return to the
 * client, approve on their behalf). One panel, rendered by every phase
 * workspace; which rows it shows comes from the shared catalog, never from
 * per-step copies. A locked state is a sentence, not a dead button (P-18).
 */

type KindState = "approved" | "approvedByStaff" | "awaiting" | "hidden";

interface KindRow {
  kind: StaffOverrideKind;
  state: Exclude<KindState, "hidden">;
  actions: StaffOverrideAction[];
  /** Static: this kind never had a reopen (a watched video). */
  reopenUnavailableKey?: string;
  /** B3, dynamic: the reopen exists but THIS client is past the point of no
   *  return, so the control is drawn closed with the reason. */
  reopenBlockedKey?: string;
}

function record(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : null;
}

function sectionState(detail: StaffDetail, key: string): string | null {
  return detail.sections.find((section) => String(section.key) === key)?.state ?? null;
}

function confirmationOf(detail: StaffDetail, kind: "profile" | "apple"): Record<string, unknown> | null {
  const confirmations = record(detail.resources.find((row) => row.key === "step_confirmations")?.value);
  return confirmations ? record(confirmations[kind]) : null;
}

function submitted(detail: StaffDetail, questionnaire: "q1" | "q2"): boolean {
  return detail.submissions.some((row) => row.questionnaire === questionnaire
    && ["waiting_for_transcripts", "ready", "exported"].includes(String(row.status)));
}

function deliverableState(detail: StaffDetail, kinds: string[]): KindState {
  const live = detail.deliverables.filter((row) =>
    (row.published === 1 || row.published === true) && kinds.includes(String(row.kind)));
  if (live.length === 0) return "hidden";
  if (live.every((row) => row.status === "approved")) {
    const ownerId = detail.client.owner?.id ?? null;
    // O5: the server sends `approvedBy` (mapKeys camel-cases every staff row).
    // Reading `approved_by` made this branch unreachable, so a material the
    // PARTNER approved on the client's behalf still said "Potvrzeno klientem".
    const byStaff = live.some((row) => typeof row.approvedBy === "string" && row.approvedBy !== ownerId);
    return byStaff ? "approvedByStaff" : "approved";
  }
  return live.some((row) => row.status === "review") ? "awaiting" : "hidden";
}

/**
 * B3: the facts the SERVER checks before it allows a reopen, read off the same
 * detail payload the panel already has. Mirrors
 * `routes/staff-confirmations.ts`: a trial link OR a training step that has
 * left `locked` is "training started"; any sales material row blocks the q2
 * reopen; a running build blocks it too.
 */
export function overrideReopenFacts(detail: StaffDetail): StaffOverrideReopenFacts {
  const trainingState = sectionState(detail, "training");
  const trialLink = detail.resources.some((row) => row.key === "trial_link");
  return {
    trainingStarted: trialLink || (trainingState !== null && trainingState !== "locked"),
    salesMaterialExists: detail.deliverables.some((row) =>
      ["plan", "salespage", "emails", "webinar"].includes(String(row.kind))),
    salesBuildRunning: (detail.dfyRuns ?? []).some((run) => String(run.status) === "running")
  };
}

export function overrideKindState(detail: StaffDetail, kind: StaffOverrideKind): KindState {
  switch (kind) {
    case "profile": {
      const confirmation = confirmationOf(detail, "profile");
      if (confirmation?.confirmed === true) return confirmation.on_behalf === true ? "approvedByStaff" : "approved";
      return "awaiting";
    }
    case "apple": {
      if (!detail.client.axes.includesApp) return "hidden";
      const confirmation = confirmationOf(detail, "apple");
      if (confirmation?.confirmed === true) return confirmation.on_behalf === true ? "approvedByStaff" : "approved";
      return "awaiting";
    }
    case "q1_submit":
    case "q2_submit": {
      const questionnaire = kind === "q1_submit" ? "q1" : "q2";
      if (submitted(detail, questionnaire)) return "approved";
      const state = sectionState(detail, questionnaire);
      return state === "next" || state === "working" ? "awaiting" : "hidden";
    }
    case "knowhow_submit": {
      const state = sectionState(detail, "knowhow");
      if (state === "done") return "approved";
      return state === "next" ? "awaiting" : "hidden";
    }
    case "video_onboarding":
    case "video_marketing": {
      const state = sectionState(detail, kind === "video_onboarding" ? "onboarding_call" : "marketing_call");
      if (state === "done") return "approved";
      return state === "next" || state === "working" ? "awaiting" : "hidden";
    }
    case "strategy":
      return deliverableState(detail, ["audit", "plan"]);
    default:
      return deliverableState(detail, [kind]);
  }
}

export function overrideRowsForStep(detail: StaffDetail, step: string): KindRow[] {
  const facts = overrideReopenFacts(detail);
  return staffOverridesForStep(step).specs.flatMap((spec) => {
    const state = overrideKindState(detail, spec.kind);
    if (state === "hidden") return [];
    return [{
      kind: spec.kind,
      state,
      actions: spec.actions,
      reopenUnavailableKey: spec.reopenUnavailableKey,
      reopenBlockedKey: spec.actions.includes("reopen")
        ? staffOverrideReopenBlockedKey(spec.kind, facts) ?? undefined
        : undefined
    }];
  });
}

export function OverridePanel(props: {
  detail: StaffDetail;
  step: string;
  t: (key: string) => string;
  run: StaffRun;
}) {
  const { detail, step, t } = props;
  const rows = overrideRowsForStep(detail, step);
  // F112: "Predani Telegram bota vratit nejde: bot uz zmenil majitele" was
  // printed on every beta, feedback and handover step, including the ones
  // where the transfer had just FAILED and the bot had not moved at all. A
  // lock is a statement about reality, so it waits for reality.
  const locked = visibleStaffOverrideLocks(step, {
    botTransferState: (() => {
      const row = detail.resources.find((entry) => entry.key === "bot_transfer_state");
      const value = row ? record(row.value) : null;
      return typeof value?.state === "string" ? value.state : null;
    })()
  });
  const overrides: StaffOverridesValue = parseStaffOverrides(
    detail.resources.find((row) => row.key === "staff_overrides")?.value
  );
  const [armed, setArmed] = useState<{ kind: StaffOverrideKind; action: StaffOverrideAction } | null>(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // O8: a step with nothing to hand back SAYS so; disappearing reads as a
  // missing panel, which is exactly how three steps came back in the audit.
  const emptyReasonKey = staffOverrideEmptyReasonKey(step);
  if (rows.length === 0 && locked.length === 0 && !emptyReasonKey) return null;

  const errorSentence = (code: string) => {
    const key = `staffConsole.override.error.${code}`;
    const sentence = t(key);
    return sentence === key ? t("staffConsole.override.error.generic") : sentence;
  };

  async function fire(kind: StaffOverrideKind, action: StaffOverrideAction) {
    const trimmed = reason.trim();
    if (trimmed.length < 3) {
      setError(t("staffConsole.override.reasonRequired"));
      return;
    }
    setBusy(true);
    setError(null);
    const path = action === "reopen"
      ? `/api/staff/clients/${detail.client.id}/confirmations/${kind}/undo`
      : `/api/staff/clients/${detail.client.id}/confirmations/${kind}/approve`;
    const response = await props.run(path, { method: "POST", body: { reason: trimmed } });
    if (response.ok) {
      setArmed(null);
      setReason("");
    } else {
      let code = "generic";
      try {
        const body = await response.json() as { error?: string };
        if (typeof body.error === "string") code = body.error;
      } catch { /* the sentence below already covers an unreadable body */ }
      setError(errorSentence(code));
    }
    setBusy(false);
  }

  return (
    <article className="staff-card override-panel">
      <h3>{t("staffConsole.override.title")}</h3>
      <p className="staff-card-hint">{t("staffConsole.override.hint")}</p>
      <ul className="override-rows">
        {rows.map((row) => {
          const label = t(`staffConsole.confirmationUndo.kind.${row.kind}`);
          const note = overrides[row.kind];
          const noteVisible = note && (
            (note.action === "reopened" && row.state === "awaiting")
            || (note.action === "approved" && row.state !== "awaiting")
          );
          const stateKey = row.state === "awaiting"
            ? "staffConsole.override.stateAwaiting"
            : row.state === "approvedByStaff"
              ? "staffConsole.override.stateApprovedByStaff"
              : "staffConsole.override.stateApproved";
          // B3: a reopen the server would refuse is not drawn as a button. The
          // reason prints below, before anybody spends a written sentence on
          // an action that ends in a 409.
          const available: StaffOverrideAction[] = row.state === "awaiting"
            ? row.actions.filter((action) => action === "approve")
            : row.reopenBlockedKey
              ? []
              : row.actions.filter((action) => action === "reopen");
          return (
            <li key={row.kind} className="override-row">
              <div className="override-row-head">
                <span className="override-row-label">
                  <strong>{label}</strong>
                  <small className={`override-state is-${row.state}`}>{t(stateKey)}</small>
                </span>
                {armed?.kind !== row.kind && (
                  <span className="override-actions">
                    {available.map((action) => (
                      <button
                        key={action}
                        className="secondary-button compact-button"
                        type="button"
                        onClick={() => { setArmed({ kind: row.kind, action }); setReason(""); setError(null); }}
                      >
                        {t(action === "reopen" ? "staffConsole.override.reopenButton" : "staffConsole.override.approveButton")}
                      </button>
                    ))}
                  </span>
                )}
              </div>
              {noteVisible && (
                <p className="override-note">
                  {t(note.action === "reopened" ? "staffConsole.override.reopenedNote" : "staffConsole.override.approvedNote")
                    .replace("{reason}", note.reason)}
                </p>
              )}
              {row.state === "approved" && !row.actions.includes("reopen") && row.reopenUnavailableKey && (
                <p className="override-locked-note">{t(row.reopenUnavailableKey)}</p>
              )}
              {row.state !== "awaiting" && row.reopenBlockedKey && (
                <p className="override-locked-note" data-reopen-blocked={row.kind}>{t(row.reopenBlockedKey)}</p>
              )}
              {armed?.kind === row.kind && (
                <div className="override-confirm" role="alert">
                  <p>
                    {t(armed.action === "reopen" ? "staffConsole.override.reopenConfirm" : "staffConsole.override.approveConfirm")
                      .replace("{item}", label)
                      .replace("{client}", detail.client.name)}
                  </p>
                  <label className="override-reason">
                    <span>{t("staffConsole.override.reasonLabel")}</span>
                    <textarea
                      rows={2}
                      value={reason}
                      placeholder={t("staffConsole.override.reasonPlaceholder")}
                      onChange={(event) => setReason(event.target.value)}
                    />
                  </label>
                  {error && <p className="error-message override-error">{error}</p>}
                  <div className="button-row">
                    <button
                      className="secondary-button compact-button"
                      type="button"
                      disabled={busy}
                      onClick={() => void fire(row.kind, armed.action)}
                    >
                      {t(armed.action === "reopen" ? "staffConsole.override.confirmReopen" : "staffConsole.override.confirmApprove")}
                    </button>
                    <button
                      className="quiet-link"
                      type="button"
                      disabled={busy}
                      onClick={() => { setArmed(null); setError(null); }}
                    >
                      {t("common.cancel")}
                    </button>
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {rows.length === 0 && locked.length === 0 && emptyReasonKey && (
        <p className="override-empty" data-override-empty={step}>{t(emptyReasonKey)}</p>
      )}
      {locked.length > 0 && (
        <div className="override-locked">
          <h4>{t("staffConsole.override.lockedTitle")}</h4>
          {locked.map((kind) => (
            <p key={kind} className="override-locked-note">{t(lockedReasonKey(kind))}</p>
          ))}
        </div>
      )}
    </article>
  );
}
